/**
 * Legacy study import — converts old single-study format with embedded studyGroup.
 */

import type { StudyStoreDeps } from './study-import';
import { convertReadingLevel, convertWritingLevel } from './reading-level-converters';

/** Legacy study format shape */
export interface LegacyStudyData {
  name: string;
  startingLocation: { testamentIndex: number; bookIndex: number; chapterIndex: number; verseIndex: number };
  endingLocation?: { testamentIndex: number; bookIndex: number; chapterIndex: number; verseIndex: number };
  writingLocation: { testamentIndex: number; bookIndex: number; chapterIndex: number; verseIndex: number };
  memoryStartingLocation?: { testamentIndex: number; bookIndex: number; chapterIndex: number; verseIndex: number };
  memoryEndingLocation?: { testamentIndex: number; bookIndex: number; chapterIndex: number; verseIndex: number };
  studyGroup: {
    id: string;
    name: string;
    leaderCount: number;
    teachers?: Array<{ id: string; name: string }>;
    students: Array<{ id: string; name: string; readingLevel: string; writingLevel: number }>;
  };
  assignments: Array<{
    student: { id: string; name: string; readingLevel: string; writingLevel: number };
    reviewQuestion: string;
    startingLocation?: { testamentIndex: number; bookIndex: number; chapterIndex: number; verseIndex: number };
    endingLocation?: { testamentIndex: number; bookIndex: number; chapterIndex: number; verseIndex: number };
  }>;
  teacherAssignments?: Array<{
    teacher: { id: string; name: string };
    reviewQuestion: string | null;
  }>;
  scenes: Array<{ descriptions: string[] }>;
}

/** Import a legacy single-study format (from old skjv-web) */
export function importLegacyStudy(
  data: LegacyStudyData,
  deps: StudyStoreDeps,
): { isStudyImported: boolean; isGroupImported: boolean } {
  let isGroupImported = false;

  // Import the study group if it doesn't exist
  const existingGroup = deps.studyGroups.find((g) => g.id === data.studyGroup.id);
  if (!existingGroup) {
    deps.studyGroups.push({
      id: data.studyGroup.id,
      name: data.studyGroup.name,
      description: '',
      leaderCount: data.studyGroup.leaderCount,
      teachers: (data.studyGroup.teachers || []).map((t) => ({ id: t.id, name: t.name })),
      students: data.studyGroup.students.map((s) => ({
        id: s.id,
        name: s.name,
        readingLevel: convertReadingLevel(s.readingLevel),
        writingLevel: convertWritingLevel(s.writingLevel),
      })),
      updatedAt: deps.now(),
    });
    deps.saveStudyGroups();
    isGroupImported = true;
  }

  // Create the study with embedded student objects in assignments
  const now = new Date().toISOString();
  deps.studies.push({
    id: crypto.randomUUID(),
    name: data.name,
    createdAt: now,
    updatedAt: now,
    startingLocation: data.startingLocation,
    endingLocation: data.endingLocation,
    studyGroupId: data.studyGroup.id,
    scenes: data.scenes,
    writingLocation: data.writingLocation,
    memoryStartingLocation: data.memoryStartingLocation,
    memoryEndingLocation: data.memoryEndingLocation,
    assignments: data.assignments.map((a) => ({
      student: {
        id: a.student.id,
        name: a.student.name,
        readingLevel: convertReadingLevel(a.student.readingLevel),
        writingLevel: convertWritingLevel(a.student.writingLevel),
      },
      reviewQuestion: a.reviewQuestion || null,
      startingLocation: a.startingLocation,
      endingLocation: a.endingLocation,
    })),
    teacherAssignments: (data.teacherAssignments || []).map((ta) => ({
      teacher: { id: ta.teacher.id, name: ta.teacher.name },
      reviewQuestion: ta.reviewQuestion || null,
    })),
  });
  deps.saveStudies();

  return { isStudyImported: true, isGroupImported };
}
